/* eslint-disable @typescript-eslint/no-non-null-assertion */
import React, { useEffect, useState } from "react";
import { autorun } from "mobx";
import {
  Box,
  Stack,
  Typography,
  useTheme,
  InputLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  ListSubheader,
} from "@mui/material";
import { LoadingButton as Button } from "@mui/lab";
import { InputGroup, NumberInput as Input } from "@/components/Form";
import { observer, inject } from "mobx-react";
import Store from "@/store";
import { UserStore } from "@/store/user";
import { OrderStore } from "@/store/orders";
import { enqueueSnackbar } from "notistack";
import { IAsset, IVWPost } from "@/interfaces";
import { useNavigate } from "react-router-dom";
import { useAccount } from "wagmi";
import { useWeb3Modal } from "@web3modal/react";
import MarketContractStore from "@/store/contract";
import { ethers } from "ethers";
import { formatedBigNumber, requestNotificationPermission } from "@/utility";
import CommonStore from "@/store/common";
import * as BN from "bignumber.js";

interface ITradeForm {
  selectPost: IVWPost;
  setSelectedPost: React.Dispatch<React.SetStateAction<IVWPost | null>>;
  userStore?: UserStore;
  orderStore?: OrderStore;
  contractStore?: MarketContractStore;
  commonStore?: CommonStore;
}
const TradeForm = inject(
  Store.type.userStore,
  Store.type.orderStore,
  Store.type.contractStore,
  Store.type.commonStore,
)(
  observer(
    ({
      selectPost,
      setSelectedPost,
      userStore,
      orderStore,
      contractStore,
      commonStore,
    }: ITradeForm) => {
      const theme = useTheme();
      const navigate = useNavigate();

      const [openDialog, setOpenDialog] = useState(false);
      const [isSubmiting, setIsSubmiting] = useState(false);
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const [asset, setAsset] = useState<IAsset | undefined>(undefined);
      const [labelInputToken, setLabelInputToken] =
        useState("I want to receive");
      const [labelInputFiat, setLabelInputFiat] = useState("I want to pay");
      const [paymentMethod, setPaymentMethod] = useState("");
      const [quantity, setQuantity] = useState("");
      const [tokenAmount, setTokenAmount] = useState("");
      const [paymentList, setPaymentList] = useState<
        (false | JSX.Element)[][] | null
      >(null);
      const { address } = useAccount();
      const { open } = useWeb3Modal();

      const handleCreateOrder = async () => {
        setIsSubmiting(true);
        if (!userStore?.isAccountVerified) {
          enqueueSnackbar(
            "Failed to create Order: Please complete the KYC process first before proceeding.",
            {
              variant: "error",
            },
          );
          setOpenDialog(false);
          setIsSubmiting(false);
          return;
        }
        const userId = userStore?.userId;
        if (orderStore && userId) {
          try {
            if (
              !userStore.wallet_address ||
              address !== userStore.wallet_address
            ) {
              if (!address) {
                await open({ route: "ConnectWallet" });
              }
              throw new Error("Please connect the wallet used to sign up.");
            }
            const hash = await contractStore?.createOrder({
              postId: selectPost.contract_post_id!,
              buyer: address,
              tokenAmount: ethers.utils.parseEther(tokenAmount).toString(),
            });

            const orderId = await orderStore.createOrder({
              post_id: selectPost.post_id,
              buyer_id:
                selectPost.post_type === "BUY" ? selectPost.created_by : userId,
              seller_id:
                selectPost.post_type === "BUY" ? userId : selectPost.created_by,
              store_id: selectPost.created_by,
              order_type: selectPost.post_type,
              asset_id: selectPost.asset_id,
              currency: selectPost.currency,
              price: selectPost.price,
              order_amount: ethers.utils.parseEther(tokenAmount).toString(),
              payment_id:
                selectPost.post_type === "BUY"
                  ? paymentMethod.split("||")[0]
                  : selectPost.payment_method,
              address: address,
              tx_hash: hash,
            });

            navigate(`/trade/orders/${orderId}`);
            enqueueSnackbar("Order created successfully.", {
              variant: "success",
            });
            requestNotificationPermission();
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
          } catch (error: any) {
            console.error(error);
            setOpenDialog(false);
            setIsSubmiting(false);
            if (
              (error.raw.message as string).includes(
                "reverted: P2PWallet: Insufficient balance",
              )
            ) {
              enqueueSnackbar(
                `Failed to create Order: Insufficient balance. Please deposit tokens.`,
                {
                  variant: "error",
                },
              );
            } else if (
              (error.raw.message as string).includes(
                "reverted: Token amount is outside the limits",
              )
            ) {
              enqueueSnackbar(
                `Failed to create Order: Amount is outside the limits.`,
                {
                  variant: "error",
                },
              );
            } else {
              enqueueSnackbar(`Failed to create Order: ${error.message}`, {
                variant: "error",
              });
            }
          }
        }
        setOpenDialog(false);
        setIsSubmiting(false);
      };
      const handleChangeTokenAmount = (e: { target: { value: string } }) => {
        if (e.target.value === "") {
          setTokenAmount("");
          setQuantity("");
          return;
        }
        BN.BigNumber.config({ DECIMAL_PLACES: 18 });
        const amount = BN.BigNumber(e.target.value);

        const price = BN.BigNumber(ethers.utils.formatEther(selectPost.price));
        const result = amount.multipliedBy(price);
        setQuantity(result.toString());
        setTokenAmount(e.target.value);
      };
      const handleChangeQuantity = (e: { target: { value: string } }) => {
        if (e.target.value === "") {
          setTokenAmount("");
          setQuantity("");
          return;
        }
        BN.BigNumber.config({ DECIMAL_PLACES: 18 });
        const amount = BN.BigNumber(e.target.value);
        const price = BN.BigNumber(ethers.utils.formatEther(selectPost.price));
        const result = amount.dividedBy(price);

        setTokenAmount(result.toString());
        setQuantity(e.target.value);
      };
      const overlapLimit = (): boolean => {
        if (
          quantity === "" ||
          quantity == "0.0" ||
          !selectPost.maxOrder ||
          !selectPost.minOrder
        )
          return true;
        const _qty = ethers.utils.parseEther(quantity);
        if (_qty.gt(selectPost.maxOrder)) {
          return true;
        } else if (_qty.lt(selectPost.minOrder)) {
          return true;
        } else {
          return false;
        }
      };
      function handleClose(): void {
        setOpenDialog(false);
      }
      useEffect(() => {
        if (selectPost) {
          setAsset(
            commonStore?.assets.find(
              (asset) => asset.asset_id === selectPost.asset_id,
            ),
          );
          if (selectPost.post_type === "BUY") {
            setLabelInputFiat("I want to receive");
            setLabelInputToken("I want to sell");
          } else {
            setLabelInputFiat("I want to pay");
            setLabelInputToken("I want to receive");
          }
        }
      }, [selectPost]);
      useEffect(() => {
        if (userStore) {
          userStore.fetchMyPayments(userStore.userId!);
        }
      }, [userStore]);
      useEffect(() => {
        const disposeAutorun = autorun(() => {
          if (commonStore && userStore) {
            const list = commonStore?.paymentMethods
              .filter((method) =>
                userStore.myPayments?.find(
                  (payment) => payment.payment_method_id === method.payment_id,
                ),
              )
              .map((method) => [
                <ListSubheader
                  key={method.payment_id}
                  sx={{
                    fontSize: theme.typography.body2.fontSize,
                    lineHeight: theme.typography.body2.lineHeight,
                    mt: "0.875rem",
                    mb: "0.375rem",
                  }}
                >
                  {method.payment_name}
                </ListSubheader>,
                ...userStore.myPayments
                  .filter(
                    (payment) =>
                      payment.payment_method_id === method.payment_id,
                  )
                  .map((payment) => {
                    const paymentAttributes = payment.payment_attributes
                      .filter(
                        (paymentAttribute) =>
                          paymentAttribute.payment_method_attribute
                            ?.preview_order,
                      )
                      .sort(
                        (a, b) =>
                          (a.payment_method_attribute?.preview_order ?? 0) -
                          (b.payment_method_attribute?.preview_order ?? 0),
                      );
                    const itemText = paymentAttributes
                      .map(
                        (paymentAttribute) => paymentAttribute.attribute_value,
                      )
                      .join(" ");
                    return (
                      <MenuItem
                        key={payment.id}
                        value={payment.id + "||" + itemText}
                        title={itemText}
                      >
                        {itemText}
                      </MenuItem>
                    );
                  }),
              ]);
            setPaymentList(list.length ? list : null);
          }
        });
        return () => {
          disposeAutorun();
        };
      }, [commonStore, userStore]);
      return (
        <Box
          bgcolor="rgba(0,0,0,0.8)"
          position="fixed"
          top="0"
          left="0"
          width="100%"
          height="100%"
          zIndex="12"
        >
          <Box
            sx={{
              borderRadius: theme.borderRadius.main,
              p: "43.78px 102px 87px 102px",
              bgcolor: theme.colors.white,
              position: "absolute",
              top: "50%",
              left: "50%",
              width: "650px",
              maxWidth: "95%",
              transform: "translate(-50%,-50%)",
            }}
          >
            <Typography
              variant="h2"
              color={theme.colors.darkFontColor}
              mb="46.26px"
            >
              How much do you
              <br /> want to trade?
            </Typography>
            <InputGroup sx={{ mb: "41px" }}>
              <InputLabel htmlFor="quantity">{labelInputFiat}</InputLabel>
              <Input
                name="quantity"
                id="quantity"
                placeholder={`${
                  selectPost.minOrder && formatedBigNumber(selectPost.minOrder)
                } - ${
                  selectPost.maxOrder && formatedBigNumber(selectPost.maxOrder)
                } ${selectPost?.currency}`}
                value={quantity}
                onChange={handleChangeQuantity}
              />
            </InputGroup>
            <InputGroup sx={{ mb: "41px" }}>
              <InputLabel htmlFor="tokenAmount">{labelInputToken}</InputLabel>
              <Input
                name="tokenAmount"
                id="tokenAmount"
                placeholder={`0.00 ${selectPost?.asset_name}`}
                value={tokenAmount}
                onChange={handleChangeTokenAmount}
              />
            </InputGroup>

            {selectPost.post_type === "BUY" && (
              <InputGroup sx={{ mb: "41px" }}>
                <TextField
                  variant="outlined"
                  fullWidth
                  label="Payment method"
                  select={true}
                  type="text"
                  required
                  name="paymentMethod"
                  id="paymentMethod"
                  value={paymentMethod}
                  onChange={(e) => {
                    console.log(e);
                    setPaymentMethod(e.target.value);
                  }}
                  sx={{
                    "& .MuiInputBase-input": {
                      pt: "19.5px",
                      pb: "20.5px",
                    },
                  }}
                >
                  {paymentList}
                  {paymentList === null && (
                    <MenuItem
                      onClick={() => {
                        navigate("/profile/payments/create");
                      }}
                    >
                      Add Payment
                    </MenuItem>
                  )}
                </TextField>
              </InputGroup>
            )}
            <Stack
              direction="row"
              justifyContent="center"
              alignItems="center"
              gap="30px"
              width="100%"
            >
              <Button onClick={() => setSelectedPost(null)} variant="outlined">
                Close
              </Button>
              <Button
                onClick={() => setOpenDialog(true)}
                loading={isSubmiting}
                disabled={overlapLimit()}
                variant="contained"
              >
                Trade
              </Button>
            </Stack>
          </Box>

          <Dialog open={openDialog} onClose={handleClose}>
            <DialogTitle
              sx={{
                fontSize: "15px",
                fontWeight: "600",
                lineHeight: "21px",
                color: "#04060F",
              }}
            >
              Create order
            </DialogTitle>
            <DialogContent>
              <Typography variant="h5" mb="22px" fontWeight="700">
                Are you sure you want to create order?
              </Typography>
              <Typography
                variant="body2"
                fontWeight="500"
                lineHeight="41px"
                mb="17.56px"
              >
                You pay:{" "}
                {selectPost.post_type === "SELL"
                  ? `${quantity} ${selectPost?.currency}`
                  : `${tokenAmount} ${selectPost?.asset_name}`}
                {/* {`${quantity} ${
                  selectPost.post_type === "SELL"
                    ? selectPost?.currency
                    : selectPost?.asset_name
                }`} */}
                <br /> You receive:{" "}
                {selectPost.post_type === "SELL"
                  ? `${tokenAmount} ${selectPost?.asset_name}`
                  : `${quantity} ${selectPost?.currency}`}
                {/* {`${tokenAmount} ${
                  selectPost.post_type === "SELL"
                    ? selectPost?.asset_name
                    : selectPost?.currency
                }`} */}
                {selectPost.post_type === "BUY" && (
                  <>
                    <br /> Payment method: {paymentMethod.split("||")[1]}
                  </>
                )}
              </Typography>
            </DialogContent>
            <DialogActions>
              <Button
                variant="outlined"
                loading={isSubmiting}
                onClick={handleClose}
                sx={{ minWidth: "190.28px" }}
              >
                No
              </Button>
              <Button
                variant="contained"
                loading={isSubmiting}
                onClick={handleCreateOrder}
                sx={{ minWidth: "190.28px" }}
              >
                Yes
              </Button>
            </DialogActions>
          </Dialog>
        </Box>
      );
    },
  ),
);

export default TradeForm;
